/**
 * F 331 CORRECTIVE — THE SLOT FACE AND THE POWER-UP FACE.
 *
 * Both are SETTINGS EDITORS registered through F 324, because the corrective is
 * right that both ARE settings:
 *
 *   - an empty credential slot is a Setting node with status `not_connected`;
 *   - a Power Up is a Setting node with a 24-hour expiry.
 *
 * Registering them means the AI Engine tab is not a special surface: it is the
 * same `GraphSettingsPanel` the rest of ?settings uses, with two extra editors
 * claimed for two prefixes. A second consumer that wants a provider slot gets it
 * for free, which is the doctrine — one primitive, many consumers.
 */
import React, { useState } from 'react';
import { Card, Group, Stack, Text, Badge, Button, Paper } from '@mantine/core';
import type { SettingEditorProps } from './SettingField';
import { PasswordInput } from '@mantine/core';
import type { ProviderSlot, CapabilityName } from '../../../core/registry/engineState';
import { registerCredentialActions, credentialActions, safeReason } from './CredentialField';
import type { ConnectOutcome } from '../../../core/ai/connectProvider';
import type { OverrideRecord, ResearchFinding } from '../../../core/registry/pipeline';

// ── the slot face ───────────────────────────────────────────────────────────

export interface SlotActions {
  investigate?: (provider: string) => void;
  busy?: string | null;
}

/**
 * The action channel for the slot face.
 *
 * The face is a SETTINGS EDITOR, so it receives only `{setting, value, onChange}`
 * and cannot take an `onInvestigate` prop. That is the right constraint — it is
 * what makes the face inheritable — but it means the action has to arrive some
 * other way. This mirrors `registerSettingEditor`: module state the page sets
 * once and any consumer of the face then shares.
 */
let SLOT_ACTIONS: SlotActions = {};

export function registerSlotActions(actions: SlotActions): void {
  SLOT_ACTIONS = actions;
}

export function slotActions(): SlotActions {
  return SLOT_ACTIONS;
}

export function SlotList({ slots, actions }: { slots: ProviderSlot[]; actions?: SlotActions }) {
  const [busy, setBusy] = useState<string | null>(actions?.busy ?? null);
  const [adding, setAdding] = useState<string | null>(null);
  const [addingBusy, setAddingBusy] = useState(false);
  const keyRef = React.useRef<HTMLInputElement | null>(null);
  /**
   * F 439 — WHAT THE LAST TEST SAID, per provider.
   *
   * The GREEN badge is the graph's own status (a capability node exists for
   * this provider). This is the EVIDENCE beside it: which model answered, how
   * long it took, and — when it failed — the provider's own reason.
   */
  const [tested, setTested] = useState<Record<string, ConnectOutcome>>({});

  // UNCONTROLLED, for the reason documented on CredentialField: a controlled
  // React input mirrors its value into the DOM `value` ATTRIBUTE, so the secret
  // is recoverable from `outerHTML` even at type="password". This was measured.
  const submitKey = async (provider: string) => {
    const actions = credentialActions();
    const connect = actions.connect;
    const save = actions.save;
    const attempted = keyRef.current?.value ?? '';
    if ((!connect && !save) || !attempted) return;
    setAddingBusy(true);
    try {
      if (connect) {
        // F 439 — SAVE, THEN TEST. The action seals the key, probes the
        // provider with it, and writes the capability node the badge reads.
        const res = await connect({ provider, name: 'pipeline', kind: 'api_key', value: attempted });
        // CLEAR FIRST: the plaintext leaves the DOM before anything else.
        if (keyRef.current) keyRef.current.value = '';
        // Redact HERE, where the attempted value is still in scope — the key
        // must not be able to come back to the screen inside a reason, even
        // if a store misbehaves and echoes it.
        setTested((t) => ({
          ...t,
          [provider]: { ...res, reason: safeReason(res.reason, attempted) },
        }));
        // On success the editor closes; on a refusal it stays open so the
        // operator can paste a corrected key into an empty field.
        if (res.ok) setAdding(null);
        return;
      }
      const res = await save({ provider, name: 'pipeline', kind: 'api_key', value: attempted });
      if (keyRef.current) keyRef.current.value = '';
      if (res.ok) setAdding(null);
    } catch (e) {
      if (keyRef.current) keyRef.current.value = '';
      setTested((t) => ({
        ...t,
        [provider]: {
          ok: false,
          provider,
          reason: safeReason(e instanceof Error ? e.message : String(e), attempted),
        },
      }));
    } finally {
      setAddingBusy(false);
    }
  };
  const go = async (p: string) => {
    setBusy(p);
    try {
      await actions?.investigate?.(p);
    } finally {
      setBusy(null);
    }
  };
  return (
    <Card withBorder padding="md" data-testid="slot-face">
      <Text fw={700} size="sm">Providers you could unlock</Text>
      <Text size="xs" c="dimmed" mb="xs">
        A slot with no key is not the same as a provider with nothing to offer.
        Investigate sends its public page through the pipeline.
      </Text>
      <Stack gap="xs" data-testid="slot-list">
        {slots.map((s) => (
          <Paper key={s.provider} withBorder p="xs" data-testid={`slot-${s.provider}`} data-status={s.status}>
            <Group justify="space-between" wrap="nowrap">
              <div style={{ minWidth: 0 }}>
                <Group gap="xs">
                  <Text size="sm" fw={600}>{s.label}</Text>
                  <Badge size="xs" variant="light" color={s.status === 'connected' ? 'green' : 'gray'}
                    data-testid={`slot-status-${s.provider}`}>
                    {s.status === 'connected' ? 'connected' : 'not_connected'}
                  </Badge>
                  {/* F 439 — THE VERDICT BADGE. The status badge above is the
                      graph's own word; this one is the test's. A green badge
                      here means a model ANSWERED with this key, and a red one
                      carries the provider's own reason for refusing it. */}
                  {tested[s.provider] ? (
                    <Badge size="xs" variant="light"
                      color={tested[s.provider]?.ok ? 'green' : 'red'}
                      data-testid={`slot-test-${s.provider}`}>
                      {tested[s.provider]?.ok ? 'key verified' : 'key rejected'}
                    </Badge>
                  ) : null}
                </Group>
                {s.note && <Text size="xs" c="dimmed" lineClamp={2}>{s.note}</Text>}
                {tested[s.provider] && !tested[s.provider]?.ok ? (
                  <Text size="xs" c="red" data-testid={`slot-test-reason-${s.provider}`}>
                    {safeReason(tested[s.provider]?.reason, '')}
                  </Text>
                ) : null}
                {tested[s.provider]?.ok ? (
                  <Text size="xs" c="dimmed" data-testid={`slot-test-detail-${s.provider}`}>
                    {tested[s.provider]?.model} answered in {tested[s.provider]?.latencyMs}ms
                  </Text>
                ) : null}
              </div>
              {adding === s.provider ? (
                <Stack gap={4} data-testid={`slot-key-editor-${s.provider}`}>
                  {/* size="xs", NOT "compact-xs": the app's custom compact-xs
                      size defines a height for Button but NOT for Input, so
                      Mantine's absolutely-positioned inner input collapsed to
                      height 0 — a field that LOOKED present and could not be
                      typed into. Measured: the slot input was h=0 while the
                      credential field's (size="xs") was h=28. */}
                  <PasswordInput size="xs" label={`Paste the ${s.label} key`}
                    ref={keyRef}
                    // F 439 — THE PASSWORD-MANAGER OPT-OUTS.
                    // `autoComplete="off"` is IGNORED on a password field by
                    // Chrome and by the extensions, which is why Bitwarden and
                    // LastPass still offered to save this field. "new-password"
                    // is the value browsers honour on a password field (it
                    // means "a new credential — do not fill it, do not offer to
                    // save it"), and the three vendor attributes are each
                    // extension's own documented opt-out.
                    // The field stays `type="password"`: F 327's masking rule is
                    // a security requirement, not a preference.
                    autoComplete="new-password"
                    data-1p-ignore="true" data-lpignore="true" data-form-type="false"
                    autoCorrect="off" autoCapitalize="off" spellCheck={false}
                    data-testid={`slot-key-input-${s.provider}`} disabled={addingBusy} />
                  <Group gap="xs">
                    <Button size="compact-xs" loading={addingBusy}
                      data-testid={`slot-key-save-${s.provider}`}
                      onClick={() => void submitKey(s.provider)}>Save</Button>
                    <Button size="compact-xs" variant="subtle" color="gray"
                      data-testid={`slot-key-cancel-${s.provider}`}
                      onClick={() => { if (keyRef.current) keyRef.current.value = ''; setAdding(null); }}>Cancel</Button>
                  </Group>
                </Stack>
              ) : null}
              {s.status !== 'connected' && adding !== s.provider && (
                <Group gap="xs" wrap="nowrap">
                  {/* F 332 — ADD KEY lives HERE, not on a credential row.
                      A credential row for a provider with no key DOES NOT EXIST:
                      `asCredentialSettingValue` requires present === true, because
                      F 327 only ever wrote a graph row for a credential that
                      exists. The place a user can add their first key for a
                      provider is the slot that says it is not connected. */}
                  {credentialActions().save && (
                    <Button size="compact-xs" variant="light"
                      data-testid={`addkey-${s.provider}`}
                      onClick={() => setAdding(s.provider)}>
                      Add Key
                    </Button>
                  )}
                  {actions?.investigate && (
                    <Button size="compact-xs" variant="light" color="blue" loading={busy === s.provider}
                      data-testid={`investigate-${s.provider}`}
                      onClick={() => void go(s.provider)}>
                      Investigate
                    </Button>
                  )}
                </Group>
              )}
            </Group>
          </Paper>
        ))}
      </Stack>
    </Card>
  );
}

export const SlotFace: React.FC<SettingEditorProps> = ({ setting, value }) => {
  const v = (value ?? {}) as { slots?: ProviderSlot[]; findings?: Record<string, ResearchFinding> };
  const slots = (v.slots ?? []).map((s) => {
    const f = v.findings?.[s.provider];
    return f?.unlocks?.length ? { ...s, unlocks: f.unlocks as CapabilityName[], note: f.summary } : s;
  });
  return <SlotList slots={slots} actions={slotActions()} />;
};

// ── the power-up face ───────────────────────────────────────────────────────

export function OverrideRow({
  override,
  active,
  onActivate,
  onClear,
}: {
  override: OverrideRecord;
  active: boolean;
  onActivate?: () => void;
  onClear?: () => void;
}) {
  const remaining = Math.max(0, override.expiresAt - Date.now());
  const hours = Math.round(remaining / 3_600_000);
  return (
    <Paper withBorder p="sm" data-testid={`override-${override.capability}`} data-active={active ? 'true' : 'false'}>
      <Group justify="space-between" wrap="nowrap">
        <div>
          <Group gap="xs">
            <Text size="sm" fw={600} tt="capitalize">{override.capability}</Text>
            <Badge size="xs" variant="light" color={active ? 'orange' : 'gray'} data-testid="override-badge">
              {active ? `power-up active · ${hours}h left` : 'not active'}
            </Badge>
          </Group>
          <Text size="xs" c="dimmed">
            {override.provider}/{override.paidModel} · ~${override.estimatedUsdPerMTokens}/M tokens · expires in {hours}h
          </Text>
        </div>
        {active
          ? <Button size="compact-xs" variant="light" color="gray" onClick={onClear}>End early</Button>
          : <Button size="compact-xs" variant="light" color="orange" data-testid={`activate-${override.capability}`} onClick={onActivate}>Power up 24h</Button>}
      </Group>
    </Paper>
  );
}

export const PowerUpFace: React.FC<SettingEditorProps> = ({ setting, value }) => {
  const v = (value ?? {}) as { override?: OverrideRecord; active?: boolean };
  if (!v.override) {
    return (
      <Card withBorder padding="sm" data-testid="powerup-face">
        <Text fw={700} size="sm">{setting.label}</Text>
        <Text size="xs" c="dimmed">no power-up override recorded</Text>
      </Card>
    );
  }
  return <OverrideRow override={v.override} active={v.active ?? false} />;
};